import { ChangeDetectionStrategy, Component, inject, Input, OnInit, ViewEncapsulation, computed } from '@angular/core';
import {
    ITask2ReportAvailableItems,
    ITask2ReportWidget,
    ITask2ReportWidgetFilter,
    ITaskPeriodDateRange,
} from '../fi-task-v2-report';
import { FiTaskV2ReportService } from '../services/fi-task-v2-report.service';
import { BehaviorSubject } from 'rxjs';
import { FiTaskReportType, FiTaskReportDateType } from '../fi-task-v2-report-enums';
import { FiTaskV2ReportHelperService } from '../services/fi-task-v2-report-helper.service';
import { FiTask2ReportDateService } from '../services/fi-task-v2-report-date.service';
import { Animations } from '@fi-shared/utils/animations';
import { FiTaskV2ReportDataService } from '../services/fi-task-v2-report-data.service';
import { FiTaskV2ReportTableFilterHelperService } from '../services/fi-task-v2-report-table-filter-helper.service';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { FiReportDetailsBaseDirective } from '@fi-shared/fi-report-widget/fi-report-widget-details-base/fi-report-details-base.directive';

@Component({
    selector: 'fi-task-v2-report-widget-details',
    templateUrl: './fi-task-v2-report-widget-details.component.html',
    styleUrl: './fi-task-v2-report-widget-details.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-task-v2-report-widget-details' },
    standalone: false,
    animations: [Animations.fadeHeight()],
})
export class FiTaskV2ReportWidgetDetailsComponent
    extends FiReportDetailsBaseDirective<FiTaskReportType, ITask2ReportWidgetFilter, ITask2ReportWidget>
    implements OnInit
{
    @Input() availableItems: ITask2ReportAvailableItems; // available roles/units/areas

    private fiTaskV2ReportHelperService = inject(FiTaskV2ReportHelperService);
    private fiTaskV2ReportService = inject(FiTaskV2ReportService);
    private fiTask2ReportDateService = inject(FiTask2ReportDateService);
    private fiTaskV2ReportDataService = inject(FiTaskV2ReportDataService);
    private fiTaskV2ReportTableFilterHelperService = inject(FiTaskV2ReportTableFilterHelperService);

    // contains available tasks for current widget + units/users/roles from availableItems, code can be changed later
    protected mergedAvailableItems$ = new BehaviorSubject<ITask2ReportAvailableItems>(undefined);

    protected taskDateRange: ITaskPeriodDateRange;
    protected timeIntervalOptions: INameIdObj[] = this.fiTaskV2ReportHelperService.taskReportDateOptions;
    protected skipInitialFilterSetIfNotEmpty = false;

    protected isFilterEmpty = computed<boolean>(() => {
        return this.fiTaskV2ReportTableFilterHelperService.isFilterEmpty(this.currentFilter());
    });

    public ngOnInit(): void {
        this.setMergedAvailableItems(this.availableItems, undefined);
        this.taskDateRange = this.fiTask2ReportDateService.getDateRange(this.widget().Id);
        this.currentFilter.set(this.getReportWidgetFilter());
        super.ngOnInit();
    }

    protected onCurrentFilterChanged(currentFilter: ITask2ReportWidgetFilter): void {
        this.currentFilter.set(currentFilter);
        this.onSelectedItemsChanged();
    }

    protected setMergedAvailableItems(
        availableItemsBase: ITask2ReportAvailableItems,
        widgetAvailableItems: ITask2ReportAvailableItems,
    ) {
        this.mergedAvailableItems$.next(
            this.fiTaskV2ReportHelperService.getMergedAvailableItems(availableItemsBase, widgetAvailableItems),
        );
    }

    protected getReportWidgetFilter(): ITask2ReportWidgetFilter {
        return this.fiTaskV2ReportHelperService.getReportWidgetFilter(this.widget());
    }

    protected saveWidgetRequest(widget?: ITask2ReportWidget): Promise<number> {
        return this.fiTaskV2ReportService.saveReportWidget(widget || this.widget());
    }

    protected resetReportContainer(): void {
        this.fiTaskV2ReportDataService.resetReportContainer();
    }

    protected onPeriodDateRangeChange(newRange: ITaskPeriodDateRange): void {
        this.fiTask2ReportDateService.setNewDateRange(this.widget().Id, newRange);
    }

    protected getReportName(): string {
        return this.fiTaskV2ReportHelperService.getTaskReportName(this.widget());
    }

    protected isOverdueReport(): boolean {
        return this.widget().ReportType === FiTaskReportType.Overdue;
    }

    protected back(): void {
        window.history.back();
    }

    protected readonly FiTaskReportDateType = FiTaskReportDateType;
    protected readonly FiTaskReportType = FiTaskReportType;
}
